// E8BallsParityClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 6) – uzupelnij zdania A/B oraz C/D.
// W pudelku N kul (1..N, N nieparzyste). Wylosowano k kul tak, ze suma dowolnych
// dwoch pozostalych jest parzysta => pozostale maja te sama parzystosc. Poniewaz
// liczb nieparzystych jest o 1 wiecej, pozostac musza nieparzyste, a wylosowano
// wszystkie parzyste.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

function ChoiceBox({ labels }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: "1.5px solid currentColor",
        borderRadius: 4,
        padding: "1px 8px",
        margin: "0 4px",
        opacity: 0.75,
        verticalAlign: "middle",
        fontSize: "0.9em",
      }}
    >
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 && <span style={{ opacity: 0.5 }}>|</span>}
          <strong>
            <InlineMath math={label} />
          </strong>
        </React.Fragment>
      ))}
    </span>
  );
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Zakresy numeracji. Pozostaje WIEKSZA grupa parzystosci (to ona jest odpowiedzia),
// bo tylko ona moze jednoznacznie wypelnic wszystkie pozostale kule (zostaje wiecej
// kul niz wynosi liczebnosc drugiej parzystosci, wiec parzystosc jest wymuszona).
// - "od 1 do N" (N nieparzyste): wiekszosc to NIEPARZYSTE -> odpowiedz nieparzyste
// - "od 2 do M" (M parzyste):    wiekszosc to PARZYSTE    -> odpowiedz parzyste
// Wszystkie maja NIEPARZYSTA dlugosc, wiec wiekszosc jest dokladnie o 1 (nigdy po rowno).
const RANGES = [
  { lo: 1, hi: 9 }, { lo: 1, hi: 11 }, { lo: 1, hi: 13 }, { lo: 1, hi: 15 },
  { lo: 2, hi: 10 }, { lo: 2, hi: 12 }, { lo: 2, hi: 14 }, { lo: 2, hi: 16 },
];

function sumOf(arr) {
  return arr.reduce((acc, n) => acc + n, 0);
}

function seriesLatex(arr) {
  if (arr.length <= 3) return arr.join("+");
  return `${arr[0]}+${arr[1]}+\\ldots+${arr[arr.length - 1]}`;
}

function buildData({ lo, hi, shuffleOptions = true }) {
  const evens = [];
  const odds = [];
  for (let n = lo; n <= hi; n += 1) (n % 2 === 0 ? evens : odds).push(n);

  // wieksza grupa zostaje w pudelku (jednoznaczna odpowiedz), mniejsza jest wylosowana
  const answerEven = evens.length > odds.length;
  const remaining = answerEven ? evens : odds;
  const drawn = answerEven ? odds : evens;
  const drawnSum = sumOf(drawn);
  const remainSum = sumOf(remaining);
  const answerWord = answerEven ? "parzystymi" : "nieparzystymi";
  const wrongWord = answerEven ? "nieparzystymi" : "parzystymi";

  // grupa 1: parzystosc pozostalych; grupa 2: suma wylosowanych
  let g1 = [
    { value: answerWord, correct: true },
    { value: wrongWord, correct: false },
  ];
  let g2 = [
    { value: drawnSum, correct: true },
    { value: remainSum, correct: false },
  ];
  if (shuffleOptions) {
    g1 = shuffle(g1);
    g2 = shuffle(g2);
  }

  const G1_LETTERS = ["A", "B"];
  const G2_LETTERS = ["C", "D"];
  const s1Correct = G1_LETTERS[g1.findIndex((x) => x.correct)];
  const s2Correct = G2_LETTERS[g2.findIndex((x) => x.correct)];

  return {
    lo,
    hi,
    total: hi - lo + 1,
    drawnCount: drawn.length,
    answerEven,
    drawn,
    remaining,
    drawnSum,
    remainSum,
    g1: g1.map((x, i) => ({ ...x, letter: G1_LETTERS[i] })),
    g2: g2.map((x, i) => ({ ...x, letter: G2_LETTERS[i] })),
    correct: { s1: s1Correct, s2: s2Correct },
  };
}

function createOriginalTask() {
  return {
    type: "E8BallsParityClosedTask",
    data: buildData({ lo: 1, hi: 11, shuffleOptions: false }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 6,
    },
    tags: ["e8", "teoria-liczb", "parzystosc", "podzielnosc"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8BallsParityClosedTask",
    data: buildData(pick(RANGES)),
    source: { variant: "random", basedOn: "E8_2026_MAJ_6" },
    tags: ["e8", "teoria-liczb", "parzystosc", "podzielnosc"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        W pudełku było <InlineMath math={`${data.total}`} /> kul ponumerowanych
        kolejnymi liczbami naturalnymi od <InlineMath math={`${data.lo}`} /> do{" "}
        <InlineMath math={`${data.hi}`} />. Z tego pudełka wylosowano{" "}
        <InlineMath math={`${data.drawnCount}`} /> kul. Suma liczb na dowolnych
        dwóch kulach, które pozostały w pudełku, jest parzysta.
      </p>
      <p>
        <strong>
          Uzupełnij zdania. Wybierz odpowiedź spośród oznaczonych literami{" "}
          <InlineMath math="A" /> i <InlineMath math="B" /> oraz odpowiedź
          spośród oznaczonych literami <InlineMath math="C" /> i{" "}
          <InlineMath math="D" />.
        </strong>
      </p>
      <p style={{ marginBottom: 4 }}>
        Po losowaniu w pudełku zostały wyłącznie kule ponumerowane liczbami{" "}
        <ChoiceBox labels={["A", "B"]} />.
      </p>
      <p style={{ marginTop: 0, marginLeft: 16 }}>
        {data.g1.map((o, index) => (
          <React.Fragment key={o.letter}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={o.letter} math={`\\text{${o.value}}`} />
          </React.Fragment>
        ))}
      </p>
      <p style={{ marginBottom: 4 }}>
        Suma liczb na <InlineMath math={`${data.drawnCount}`} /> wylosowanych kulach
        jest równa <ChoiceBox labels={["C", "D"]} />.
      </p>
      <p style={{ marginTop: 0, marginLeft: 16 }}>
        {data.g2.map((o, index) => (
          <React.Fragment key={o.letter}>
            {index > 0 && <span style={{ marginLeft: 36 }} />}
            <ChoiceOptionMath letter={o.letter} math={`${o.value}`} />
          </React.Fragment>
        ))}
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const answer = userAnswer || {};
  return (
    <div style={{ display: "grid", rowGap: 14, marginTop: 10 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math={data.g1[0].letter} /> lub{" "}
          <InlineMath math={data.g1[1].letter} />:
        </div>
        <ABCDInput
          options={data.g1.map((o) => ({
            label: o.letter,
            textNode: <InlineMath math={o.letter} />,
          }))}
          value={answer.s1 ?? null}
          onChange={(s1) => onChange({ ...answer, s1 })}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4, fontSize: 13, opacity: 0.7 }}>
          Wybierz <InlineMath math={data.g2[0].letter} /> lub{" "}
          <InlineMath math={data.g2[1].letter} />:
        </div>
        <ABCDInput
          options={data.g2.map((o) => ({
            label: o.letter,
            textNode: <InlineMath math={o.letter} />,
          }))}
          value={answer.s2 ?? null}
          onChange={(s2) => onChange({ ...answer, s2 })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return (
    data.g1[userAnswer?.s1]?.letter === data.correct.s1 &&
    data.g2[userAnswer?.s2]?.letter === data.correct.s2
  );
}

export function renderAnswer({ data }) {
  return (
    <InlineMath
      math={`\\text{${data.correct.s1}},\\ \\text{${data.correct.s2}}`}
    />
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  const l1 = data.g1[userAnswer?.s1]?.letter;
  const l2 = data.g2[userAnswer?.s2]?.letter;
  if (!l1 && !l2) return <i>(brak odpowiedzi)</i>;
  return (
    <InlineMath math={`\\text{${l1 || "-"}},\\ \\text{${l2 || "-"}}`} />
  );
}

export function renderExplanation({ data }) {
  const { lo, hi, drawn, remaining, drawnSum, answerEven } = data;
  const majCount = remaining.length; // pozostale (odpowiedz)
  const minCount = drawn.length; // wylosowane
  const oddsCount = answerEven ? minCount : majCount;
  const evensCount = answerEven ? majCount : minCount;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Suma dwóch liczb jest parzysta tylko wtedy, gdy obie
        są tej samej parzystości. Zatem wszystkie kule, które zostały, mają tę samą
        parzystość.
      </p>
      <p>
        <strong>Krok 2.</strong> Wśród liczb od <InlineMath math={`${lo}`} /> do{" "}
        <InlineMath math={`${hi}`} /> jest <InlineMath math={`${oddsCount}`} /> liczb
        nieparzystych i <InlineMath math={`${evensCount}`} /> parzystych. Pozostało{" "}
        <InlineMath math={`${majCount}`} /> kul, więc muszą to być liczby{" "}
        <strong>{answerEven ? "parzyste" : "nieparzyste"}</strong> (liczb{" "}
        {answerEven ? "nieparzystych" : "parzystych"} jest tylko{" "}
        <InlineMath math={`${minCount}`} />).
      </p>
      <p>
        <strong>Krok 3.</strong> Wylosowano więc wszystkie liczby{" "}
        {answerEven ? "nieparzyste" : "parzyste"}, a ich suma to:
      </p>
      <BlockMath math={`${seriesLatex(drawn)}=${drawnSum}`} />
      <p>
        Poprawne odpowiedzi:{" "}
        <InlineMath
          math={`\\text{${data.correct.s1}}\\ \\text{i}\\ \\text{${data.correct.s2}}`}
        />
        .
      </p>
    </div>
  );
}
